import {
  faCheckCircle,
  faDoorOpen,
  faUserPlus,
  faUserDoctor,
  faRefresh,
  faTimesCircle,
  faTriangleExclamation,
  faUserMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import queryString from 'query-string';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Row,
  Col,
  Container,
  Card,
  CardBody,
  CardTitle,
  Spinner,
} from 'reactstrap';
import urljoin from 'url-join';

import { AutoFocusHeader } from '../../../_components/auto-focus-header';
import { Button } from '../../../_components/button';
import { useConfig } from '../../../_components/config';
import { ClipboardCopy } from '../../../_components/copy';
import { parseTextWithURL } from '../../../_components/parse-text-with-url';
import { useToastr } from '../../../_components/toastr';
import { useCheckSession } from '../../../_components/use-check-session';

import { useSteps } from './steps/use-steps';

export interface SubmissionResult {
  precertificationId: string;
  submittedAt: string;
  error?: string;
  caution?: string;
  retries?: number;
  processingId?: string;
}

export function Submission({
  submissionResult,
  setSubmitted,
}: {
  submissionResult: SubmissionResult;
  setSubmitted: (state: boolean) => void;
}) {
  const { getValues, reset } = useFormContext();
  const { steps } = useSteps();
  const navigate = useNavigate();
  const { api, maxSubmitAttempts } = useConfig();
  const { search } = useLocation();
  const { sessionId } = queryString.parse(search);
  const toast = useToastr();
  const { isLoading, mutateAsync: signOut } = useMutation({
    mutationFn: async () =>
      axios({
        url: urljoin(api, 'signOut'),
        method: 'post',
        withCredentials: true,
        data: {
          sessionId: sessionId || null,
        },
      }),
    onSuccess: () => {
      navigate('/compliance');
      setSubmitted(false);
    },
    onError: (err) => {
      toast.error('Failed to return user to compliance page', {
        details: err instanceof Error && err?.message,
      });
    },
  });
  const { extend } = useCheckSession();
  const veteranInfo = {
    veteranReferralId: getValues('veteranReferralId'),
    veteranIcn: getValues('veteranIcn'),
    procedureCode: getValues('procedureCode'),
    requestType: getValues('requestType'),
    episodicCare: getValues('episodicCare'),
    veteranFirstName: getValues('veteranFirstName'),
    veteranLastName: getValues('veteranLastName'),
    veteranDob: getValues('veteranDob'),
    veteranGender: getValues('veteranGender'),
  };

  return (
    <Container className="py-4 text-center" fluid>
      <div className="mx-auto my-5 col-md-9 col-lg-9">
        <Card className="shadow-sm pb-3 mx-auto">
          <CardBody className="my-2 mx-1" role="main">
            {submissionResult?.error ? (
              <>
                <CardTitle>
                  <AutoFocusHeader
                    className="mt-0 mt-sm-3 mb-3 mb-sm-5"
                    id="main-content"
                  >
                    Submission Failed
                  </AutoFocusHeader>
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    size="8x"
                    className="text-red mb-1 mb-sm-5"
                  />
                </CardTitle>
                <div className="d-flex justify-content-center">
                  <p style={{ maxWidth: '700px' }}>
                    {parseTextWithURL(submissionResult?.error)}
                  </p>
                </div>
                {submissionResult?.processingId && (
                  <div className="text-muted small mb-2">
                    PID: {submissionResult.processingId}
                  </div>
                )}

                <div className="d-flex justify-content-center fw-bold">
                  <p style={{ maxWidth: '500px' }}>
                    You have{' '}
                    {(maxSubmitAttempts ?? 4) - (submissionResult.retries ?? 0)}{' '}
                    retry attempt(s) remaining. If you run out of retries, your
                    session will expire and you will have to login again.
                  </p>
                </div>
              </>
            ) : (
              <>
                <CardTitle>
                  <AutoFocusHeader
                    className="mt-0 mt-sm-3 mb-3 mb-sm-5"
                    id="main-content"
                  >
                    Submission Successful
                  </AutoFocusHeader>
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    size="8x"
                    className="text-green mb-1 mb-sm-5"
                  />
                  <div className="h4 mb-3 fw-bold">
                    Thank you for submitting this precertification notification.
                  </div>
                  {submissionResult?.processingId && (
                    <div className="text-muted small mb-2">
                      PID: {submissionResult.processingId}
                    </div>
                  )}
                </CardTitle>
                <div>
                  <div className="d-flex justify-content-center">
                    <p style={{ maxWidth: '700px' }}>
                      Your precertification notification has been submitted
                      successfully and a precertification number has been
                      generated by the Veterans Health Administration (VHA).
                      Please proceed with rendering the requested services as
                      clinically appropriate.
                    </p>
                  </div>
                  {submissionResult?.caution && (
                    <div className="d-flex justify-content-center">
                      <p style={{ maxWidth: '700px' }}>
                        <FontAwesomeIcon
                          icon={faTriangleExclamation}
                          color="orange"
                          size="lg"
                          className="me-2"
                        />
                        <span className="fw-bold">PLEASE READ:</span>{' '}
                        {submissionResult?.caution}
                      </p>
                    </div>
                  )}
                  Please make note of the Precertification ID number below.
                  <Row className="mt-4 justify-content-center">
                    <Col xs="auto" className="mb-3">
                      <b>Precertification ID: </b>
                      <span className="text-break">
                        {submissionResult.precertificationId}
                      </span>
                      <ClipboardCopy
                        text={submissionResult.precertificationId}
                        className="ms-1 mb-1"
                        label="Copy to clipboard"
                        description="Press enter to copy the precertification ID to the clipboard."
                      />
                    </Col>
                    <Col xs="auto" className="mb-3">
                      <b>Last Update Date: </b>
                      <span className="text-break">
                        {new Date(
                          submissionResult.submittedAt
                        ).toLocaleDateString()}
                      </span>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-center">
                    <p style={{ maxWidth: '700px' }}>
                      A unique precertification notification ID is required for
                      each individual service requiring precertification. Select{' '}
                      <span className="fw-bold">Add Another Procedure</span> to
                      add another bill/service code scheduled for the same Date
                      of Service and Referral Number to obtain additional
                      precertification ID numbers. It is not necessary to
                      specify additional services for episodic care such as
                      physical therapy as one notification is sufficient for the
                      entire episode of care.
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardBody>
          <hr aria-hidden="true" />
          <Container>
            <Row>
              <Col sm={12} className="text-center my-1" md={2}>
                <Button
                  aria-description="Press Enter to sign out"
                  color="primary"
                  disabled={isLoading}
                  onClick={async () => {
                    await signOut();
                    toast.success(
                      <span>
                        Signed out. Please request a new session if needed.
                      </span>
                    );
                  }}
                >
                  Sign Out
                  {isLoading ? (
                    <Spinner size="sm" className="ms-2" />
                  ) : (
                    <FontAwesomeIcon icon={faDoorOpen} className="ms-2" />
                  )}
                </Button>
              </Col>
              <Col className="text-center my-1" sm={12} md={3}>
                <Button
                  aria-description="Press Enter to add a precertification for a new Veteran"
                  aria-label="Select Another Veteran"
                  role="button"
                  color="primary"
                  disabled={isLoading}
                  onClick={() => {
                    reset({ diagnosisCodes: [], serviceRender: true });
                    extend();
                    navigate(steps[0]?.path);
                    setSubmitted(false);
                    toast.success(<span>Please specify a new veteran.</span>);
                  }}
                >
                  Select Another Veteran
                  <FontAwesomeIcon icon={faUserPlus} className="ms-2" />
                </Button>
              </Col>
              <Col className="text-center my-1" sm={12} md={2}>
                <Button
                  color="primary"
                  disabled={isLoading}
                  aria-description="Press Enter to add another procedure for the same Veteran"
                  onClick={() => {
                    reset({
                      ...veteranInfo,
                      diagnosisCodes: [],
                      serviceRender: true,
                    });
                    extend();
                    const precertDetail = steps.find(
                      (m) => m.path === 'precertification-detail'
                    );
                    navigate(precertDetail?.path || 'precertification-detail');
                    setSubmitted(false);
                    toast.success(<span>Please specify a new procedure.</span>);
                  }}
                >
                  Add Another Procedure
                  <FontAwesomeIcon icon={faUserDoctor} className="ms-2" />
                </Button>
              </Col>
              <Col className="text-center my-1" sm={12} md={3}>
                <Button
                  onClick={() => {
                    navigate('/update');
                  }}
                  color="primary"
                  aria-label="Update"
                  aria-description="Press enter to update an existing Precertification"
                >
                  Update an Existing Precertification
                  <FontAwesomeIcon
                    icon={faUserMagnifyingGlass}
                    className="ms-2"
                  />
                </Button>
              </Col>
              <Col className="text-center my-1" sm={12} md={2}>
                <Button
                  color="primary"
                  aria-description="Press Enter to retry this submission"
                  disabled={!submissionResult?.error}
                  onClick={() => {
                    extend();
                    const precertDetail = steps.find(
                      (m) => m.path === 'new-request'
                    );
                    navigate(precertDetail?.path || 'new-request');
                    setSubmitted(false);
                    toast.success(
                      <span>
                        Please review all fields and retry your submission.
                      </span>
                    );
                  }}
                >
                  Retry
                  <FontAwesomeIcon icon={faRefresh} className="ms-2" />
                </Button>
              </Col>
            </Row>
          </Container>
        </Card>
      </div>
    </Container>
  );
}
